import { AvatarOwnProps, Avatar as MuiAvatar, AvatarProps as MuiAvatarProps } from '@mui/material'
import { useMemo } from 'react'
import { useUsersAndGroupsLdFlags } from '../launch-darkly'
import { colourFromIdentity } from './colourFromIdentity'

export interface InitialsAvatarProps extends Omit<MuiAvatarProps, 'children' | 'size'> {
  fullName: string
  size?: AvatarOwnProps['size']
  /**
   * Stable id of the user/group/contact this avatar represents, used to derive a consistent
   * colour. The same subject must always pass the same id, or its colour will diverge across
   * screens — pass the subject's own domain id, never a row/option/join id. Only takes effect
   * when RELEASE_USER_AVATAR_COLOURS is on.
   */
  colourSubjectId?: string | null
  isArchived?: boolean
}

// Archived wins; otherwise the per-subject hash applies only when the flag is on;
// otherwise nothing, so the caller's own background stands.
export const resolveAvatarColourSx = ({
  flagOn,
  colourSubjectId,
  isArchived,
}: {
  flagOn: boolean
  colourSubjectId?: string | null
  isArchived?: boolean
}): { backgroundColor?: string; color?: string; border?: string; borderColor?: string } => {
  if (isArchived) {
    return { backgroundColor: 'bg.surface.default', color: 'text.muted', border: '1px solid', borderColor: 'border.strongest' }
  }
  if (flagOn && colourSubjectId) return { backgroundColor: colourFromIdentity(colourSubjectId), color: 'text.inverted' }
  return {}
}

interface AvatarProps extends Omit<MuiAvatarProps, 'children' | 'size'> {
  text: string
  size?: AvatarOwnProps['size']
}
export const Avatar = (props: AvatarProps) => {
  const { text, sx = {}, ...avatarProps } = props

  return (
    <MuiAvatar
      {...avatarProps}
      sx={{
        backgroundColor: 'primary.main',
        '&:not(.MuiAvatar-colorDefault)': { backgroundColor: 'transparent' },
        ...sx,
      }}
    >
      {text}
    </MuiAvatar>
  )
}

export const InitialsAvatar: React.FC<InitialsAvatarProps> = ({ fullName, colourSubjectId, isArchived, sx = {}, ...props }) => {
  // TODO: We've experienced errors where `fullName` can be `undefined`. Further investigation is needed.
  // Defaulting to an empty string while investigation is on-going to prevent crashes.
  // Sentry Error: https://omnea.sentry.io/share/issue/529478ca0e024b468eb4f070d84238b0/
  const initials = useMemo(() => getInitials(fullName ?? ''), [fullName])
  const { RELEASE_USER_AVATAR_COLOURS } = useUsersAndGroupsLdFlags()
  const colourSx = resolveAvatarColourSx({ flagOn: !!RELEASE_USER_AVATAR_COLOURS, colourSubjectId, isArchived })
  return <Avatar data-testid={`user-avatar-${fullName}`} {...props} sx={{ ...sx, ...colourSx }} text={initials} />
}

// Regex for non-alphanumeric or space characters
const nonAlphanumericRegex = /[^a-zA-Z0-9]/g

export function getInitials(fullName: string) {
  // Drop display annotations like "(Me)" or "(Inactive)" so they aren't read as the surname.
  const [firstName, ...surnames] = fullName
    .replace(/\([^)]*\)/g, ' ')
    .trim()
    .split(/\s+/)

  const firstInitial = firstName?.replace(nonAlphanumericRegex, '').charAt(0) ?? ''
  const lastInitial = surnames[surnames.length - 1]?.replace(nonAlphanumericRegex, '').charAt(0) ?? ''

  return `${firstInitial}${lastInitial}`.toUpperCase() || '!'
}
